import {
  collection,
  deleteField,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  QueryDocumentSnapshot,
  setDoc,
  startAfter,
  Timestamp,
  UpdateData,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

import { Constraints } from '@superfunnel/types/constraints';
import { DynamicPage } from '@superfunnel/types/dynamic-page';
import { DynamicPageVariant } from '@superfunnel/types/dynamic-page-variant';
import {
  DynamicPageVersion,
  DynamicPageVersionDiff,
  DynamicPageVersionMetadata,
  VersionIssue,
} from '@superfunnel/types/dynamic-page-version';
import { Website } from '@superfunnel/types/website';
import {
  NormalizedEntityDiff,
  RawTextChange,
} from '@superfunnel/utils/version-diff-normalize';

import { db, functions } from '../Firebase';
import { emptyConverter } from '../utils';

type NewDynamicPageVariantStatus = Extract<
  DynamicPageVariant['status'],
  'draft' | 'active'
>;

export const dynamicPagesColRef = (accountId: string) => {
  return collection(db, 'accounts', accountId, 'campaigns').withConverter(
    emptyConverter<DynamicPage>(),
  );
};

export const dynamicPagesQuery = (accountId: string) => {
  return query(
    dynamicPagesColRef(accountId),
    where('status', 'in', ['active', 'disabled', 'draft']),
    orderBy('createdAt', 'desc'),
  );
};

export const getDynamicPageWebsitesQuery = (
  accountId: string,
  dynamicPageId: string,
) =>
  query(
    collection(
      db,
      'accounts',
      accountId,
      'campaigns',
      dynamicPageId,
      'websites',
    ).withConverter(emptyConverter<Website>()),
    where('status', 'in', ['draft', 'active', 'disabled', 'creating']),
    orderBy('createdAt', 'asc'),
  );

export const createNewDynamicPageVariant = async (
  accountId: string,
  dynamicPageId: string,
  name: string,
  description: string | undefined,
  constraints: Constraints,
  status: NewDynamicPageVariantStatus = 'draft',
): Promise<{ variantId: string; websiteId: string }> => {
  const fn = httpsCallable<unknown, { variantId: string; websiteId: string }>(
    functions,
    'accounts/campaigns/addVariant',
  );

  const res = await fn({
    accountId,
    dynamicPageId,
    name,
    description,
    constraints,
    status,
  });

  return res.data;
};

export const updateWebsiteStatus = async (
  accountId: string,
  dynamicPageId: string,
  websiteId: string,
  status: 'active' | 'disabled',
): Promise<any> => {
  const websiteRef = doc(
    db,
    'accounts',
    accountId,
    'campaigns',
    dynamicPageId,
    'websites',
    websiteId,
  );

  return updateDoc(websiteRef, { status });
};

/**
 * Promote a generated website out of draft, together with the variant that
 * serves it and its page.
 *
 * All three moves go in one batch, so they either all land or none do.
 * Publishing only reads `active` variants and websites, so a variant left
 * behind at `draft` makes the page unpublishable for good — the publish
 * preview answers "Dynamic page does not have any variants" while the variant
 * sits visible in the dashboard, and nothing repairs it. As three sequential
 * writes this happened whenever the tab closed or a write failed partway; a
 * failed batch leaves the page untouched and the next attempt retries cleanly.
 *
 * The variant is resolved from the website id, the lookup both callers were
 * doing by hand.
 */
export const moveWebsiteFromDraft = async (
  accountId: string,
  dynamicPageId: string,
  websiteId: string,
): Promise<void> => {
  const dynamicPageRef = doc(
    db,
    'accounts',
    accountId,
    'campaigns',
    dynamicPageId,
  );
  const websiteRef = doc(dynamicPageRef, 'websites', websiteId);

  const [dynamicPageSnap, websiteSnap, owningVariantsSnap] = await Promise.all([
    getDoc(dynamicPageRef.withConverter(emptyConverter<DynamicPage>())),
    getDoc(websiteRef.withConverter(emptyConverter<Website>())),
    getDocs(
      query(
        collection(dynamicPageRef, 'variants').withConverter(
          emptyConverter<DynamicPageVariant>(),
        ),
        where('websiteIds', 'array-contains', websiteId),
      ),
    ),
  ]);

  const batch = writeBatch(db);

  if (websiteSnap.data()?.status === 'draft') {
    batch.update(websiteRef, { status: 'active' });
  }

  owningVariantsSnap.docs
    .filter((variant) => variant.data().status === 'draft')
    .forEach((variant) => batch.update(variant.ref, { status: 'active' }));

  batch.update(dynamicPageRef, {
    // A page still in draft has never been live; `disabled` is the state it
    // holds until the first publish. A live page must keep its own status.
    ...(dynamicPageSnap.data()?.status === 'draft'
      ? { status: 'disabled' }
      : {}),
    hasChangesToPublish: true,
    lastEditedAt: Timestamp.now(),
  });

  await batch.commit();
};

/**
 * Clear the development data of a website so it starts fresh.
 */
export const clearWebsiteDevelopment = async (
  accountId: string,
  dynamicPageId: string,
  websiteId: string,
): Promise<void> => {
  const websiteRef = doc(
    db,
    'accounts',
    accountId,
    'campaigns',
    dynamicPageId,
    'websites',
    websiteId,
  );

  await updateDoc(websiteRef, {
    'development.sections': [],
    'development.popups': [],
    'development.banners': [],
    'updatedAt': Timestamp.now(),
  });
};

export const duplicateDynamicPage = async (
  accountId: string,
  dynamicPageId: string,
): Promise<{ campaignId: string }> => {
  const fn = httpsCallable<any, { campaignId: string }>(
    functions,
    'accounts/campaigns/duplicateCampaign',
  );

  const res = await fn({
    accountId,
    campaignId: dynamicPageId,
  });

  return res.data;
};

/**
 * Copy the development data from a source website to a target website.
 * Used when generating a variant based on an existing variant.
 */
export const copyWebsiteDevelopment = async (
  accountId: string,
  sourceCampaignId: string,
  sourceWebsiteId: string,
  targetCampaignId: string,
  targetWebsiteId: string,
): Promise<{ ok: boolean }> => {
  // Get source website document
  const sourceWebsiteRef = doc(
    db,
    'accounts',
    accountId,
    'campaigns',
    sourceCampaignId,
    'websites',
    sourceWebsiteId,
  ).withConverter(emptyConverter<Website>());

  const sourceWebsiteDoc = await getDoc(sourceWebsiteRef);
  const sourceWebsiteData = sourceWebsiteDoc.data();

  if (!sourceWebsiteData) {
    throw new Error('Source website not found');
  }

  if (!sourceWebsiteData.development) {
    throw new Error('Source website has no development data');
  }

  // Update target website with the source development data
  const targetWebsiteRef = doc(
    db,
    'accounts',
    accountId,
    'campaigns',
    targetCampaignId,
    'websites',
    targetWebsiteId,
  );

  await updateDoc(targetWebsiteRef, {
    development: sourceWebsiteData.development,
    updatedAt: Timestamp.now(),
    changesToPublish: true,
  });

  return { ok: true };
};

export const updateDynamicPageHasPendingPublishChanges = async (
  accountId: string,
  dynamicPageId: string,
  hasChanges: boolean,
): Promise<void> => {
  const dynamicPageRef = doc(dynamicPagesColRef(accountId), dynamicPageId);

  return setDoc(
    dynamicPageRef,
    {
      hasChangesToPublish: hasChanges,
      ...(hasChanges ? { lastEditedAt: Timestamp.now() } : {}),
    },
    { merge: true },
  );
};

export const getPrevDynamicPageVersion = async (
  accountId: string,
  dynamicPageId: string,
): Promise<QueryDocumentSnapshot<DynamicPageVersionMetadata>[]> => {
  const dynamicPageDocRef = doc(
    db,
    'accounts',
    accountId,
    'campaigns',
    dynamicPageId,
  );

  const dynamicPageVersionsColRef = collection(
    dynamicPageDocRef,
    'versions',
  ).withConverter(emptyConverter<DynamicPageVersionMetadata>());

  return getDocs(
    query(
      dynamicPageVersionsColRef,
      where('type', '==', 'dynamicPageVersion'),
      orderBy('createdAt', 'desc'),
      limit(1),
    ),
  ).then((querySnapshot) => querySnapshot.docs);
};

/**
 * Redeploy a previous version: creates a new version on the backend that
 * copies the snapshot of `sourceVersionId` and becomes the current version.
 */
export const redeployDynamicPageVersion = async (
  accountId: string,
  dynamicPageId: string,
  sourceVersionId: string,
): Promise<{ id: string }> => {
  const fn = httpsCallable<unknown, { id: string }>(
    functions,
    'accounts/campaigns/versions/redeployDynamicPageVersion',
    { timeout: 60000 },
  );

  const res = await fn({
    accountId,
    dynamicPageId,
    sourceVersionId,
  });

  return res.data;
};

/**
 * Revert a page's draft (development) state to a previous version's snapshot
 * without publishing. Does NOT create a new version or touch production; the
 * page is left with unpublished changes for the user to review and publish.
 *
 * @param revertForms When true, also restores the development content of the
 *   forms / quiz forms referenced by the version.
 */
export const revertDynamicPageVersion = async (
  accountId: string,
  dynamicPageId: string,
  sourceVersionId: string,
  revertForms: boolean,
): Promise<{ ok: true }> => {
  const fn = httpsCallable<unknown, { ok: true }>(
    functions,
    'accounts/campaigns/versions/revertDynamicPageVersion',
    { timeout: 60000 },
  );

  const res = await fn({
    accountId,
    dynamicPageId,
    sourceVersionId,
    revertForms,
  });

  return res.data;
};

export const createDynamicPageFromVersion = async (
  accountId: string,
  dynamicPageId: string,
  sourceVersionId: string,
): Promise<{ dynamicPageId: string; quizId?: string }> => {
  const fn = httpsCallable<unknown, { dynamicPageId: string; quizId?: string }>(
    functions,
    'accounts/campaigns/versions/createDynamicPageFromVersion',
    { timeout: 60000 },
  );

  const res = await fn({ accountId, dynamicPageId, sourceVersionId });
  return res.data;
};

/**
 * Get the full contents of a dynamic page version from Cloud Storage.
 * Use this when displaying version details, not for listing versions.
 */
export const getDynamicPageVersionContents = async (
  accountId: string,
  dynamicPageId: string,
  versionId: string,
): Promise<DynamicPageVersion> => {
  const fn = httpsCallable<unknown, DynamicPageVersion>(
    functions,
    'accounts/campaigns/versions/getDynamicPageVersion',
    { timeout: 60000 },
  );

  const res = await fn({
    accountId,
    dynamicPageId,
    versionId,
  });

  return res.data;
};

export const getDynamicPageVersions = async (
  accountId: string,
  dynamicPageId: string,
  _limit: number,
  last?: QueryDocumentSnapshot<DynamicPageVersionMetadata>,
): Promise<QueryDocumentSnapshot<DynamicPageVersionMetadata>[]> => {
  const dynamicPageDocRef = doc(
    db,
    'accounts',
    accountId,
    'campaigns',
    dynamicPageId,
  );

  const dynamicPageVersionsColRef = collection(
    dynamicPageDocRef,
    'versions',
  ).withConverter(emptyConverter<DynamicPageVersionMetadata>());

  return getDocs(
    query(
      dynamicPageVersionsColRef,
      where('type', '==', 'dynamicPageVersion'),
      orderBy('createdAt', 'desc'),
      ...(last ? [startAfter(last)] : []),
      limit(_limit),
    ),
  ).then((querySnapshot) => querySnapshot.docs);
};

export const updateDynamicPage = async (
  accountId: string,
  dynamicPageId: string,
  update: UpdateData<
    Pick<
      DynamicPage,
      | 'name'
      | 'description'
      | 'config'
      | 'brand'
      | 'status'
      | 'tags'
      | 'connections'
    >
  >,
  mergeFields?: string[],
): Promise<any> => {
  const dynamicPageDocRef = doc(
    db,
    'accounts',
    accountId,
    'campaigns',
    dynamicPageId,
  );

  const res = await setDoc(
    dynamicPageDocRef,
    update,
    mergeFields ? { mergeFields } : { merge: true },
  );

  updateDynamicPageHasPendingPublishChanges(accountId, dynamicPageId, true);

  return res;
};

export const deleteDynamicPageFields = async (
  accountId: string,
  dynamicPageId: string,
  fields: readonly string[],
): Promise<void> => {
  if (fields.length === 0) return;

  const dynamicPageDocRef = doc(
    db,
    'accounts',
    accountId,
    'campaigns',
    dynamicPageId,
  );
  const update = Object.fromEntries(
    fields.map((field) => [field, deleteField()]),
  );

  await updateDoc(dynamicPageDocRef, update);
  updateDynamicPageHasPendingPublishChanges(accountId, dynamicPageId, true);
};

export const updateDynamicPageMetadata = async (
  accountId: string,
  dynamicPageId: string,
  update: { path?: string; alternatePathes?: string[] },
): Promise<any> => {
  const fn = httpsCallable<any, any>(
    functions,
    'accounts/campaigns/updateCampaign',
  );

  const res = await fn({
    accountId,
    campaignId: dynamicPageId,
    update,
  });

  updateDynamicPageHasPendingPublishChanges(accountId, dynamicPageId, true);

  return res.data;
};

const CONVERSION_GOAL_FIELDS = new Set([
  'conversionGoalEventId',
  'secondaryConversionEventIds',
]);

// Page metadata fields (name/description/audience/path/conversion goal) are not
// client-writable per Firestore rules, so they must be persisted through the
// server-side updateCampaign callable.
export const updateDynamicPageFields = async (
  accountId: string,
  dynamicPageId: string,
  update: {
    name?: string;
    description?: string;
    audience?: string;
    conversionGoalEventId?: string | null;
    secondaryConversionEventIds?: string[];
    path?: string;
    alternatePathes?: string[];
  },
): Promise<any> => {
  const fn = httpsCallable<any, any>(
    functions,
    'accounts/campaigns/updateCampaign',
  );

  const res = await fn({
    accountId,
    campaignId: dynamicPageId,
    update,
  });

  if (Object.keys(update).some((key) => !CONVERSION_GOAL_FIELDS.has(key))) {
    updateDynamicPageHasPendingPublishChanges(accountId, dynamicPageId, true);
  }

  return res.data;
};

export type ConversionGoalUpdate = Pick<
  DynamicPage,
  'conversionGoalEventId' | 'secondaryConversionEventIds'
>;

// The conversion goal only changes how analytics are counted, so unlike the
// other metadata fields it leaves nothing to publish.
export const updateDynamicPageConversionGoal = async (
  accountId: string,
  dynamicPageId: string,
  update: ConversionGoalUpdate,
) => {
  const fn = httpsCallable(functions, 'accounts/campaigns/updateCampaign');
  await fn({ accountId, campaignId: dynamicPageId, update });
};

export const deleteDynamicPage = async (
  accountId: string,
  dynamicPageId: string,
): Promise<{ success: boolean }> => {
  const fn = httpsCallable<any, { success: boolean }>(
    functions,
    'accounts/dynamicPages/deleteDynamicPage',
  );

  const res = await fn({
    accountId,
    dynamicPageId,
  });

  return res.data;
};

export const unpublishDynamicPage = async (
  accountId: string,
  dynamicPageId: string,
): Promise<{ success: boolean }> => {
  const fn = httpsCallable<any, { success: boolean }>(
    functions,
    'accounts/campaigns/unpublishCampaign',
  );

  const res = await fn({
    accountId,
    campaignId: dynamicPageId,
  });

  return res.data;
};

export const getAllDynamicPagesLinks = async (
  accountId: string,
  dynamicPageIds: string[],
): Promise<string[]> => {
  const fn = httpsCallable<any, any>(
    functions,
    'accounts/campaigns/linkReplacer/getAllLinks',
    { timeout: 600000 },
  );

  const res = await fn({
    accountId,
    campaignIds: dynamicPageIds,
  });

  return res.data;
};

/** One changed entity in a publish preview, as summarized by the server. */
export interface PublishPreviewChange {
  entity: DynamicPageVersionDiff['entity'];
  type: DynamicPageVersionDiff['type'];
  id: string;
  name: string;
  explanation: string;
}

export interface PublishPreview {
  previewId: string;
  /** The page has no live version yet, so `diffSummary` has nothing to list. */
  isInitialPublish: boolean;
  diffSummary: PublishPreviewChange[];
  issues?: VersionIssue[];
}

/**
 * Builds a publish preview server-side. The full version blob stays on the
 * server (persisted under `previewId` with a 24h TTL) and we get back only a
 * compact summary of what would change. Publish later with
 * {@link publishPreview}.
 *
 * Pass `quizId` when publishing an independent quiz, so the snapshot captures
 * the quiz's draft forms instead of its live ones and the diff is reported step
 * by step instead of as one "quiz changed" row.
 */
export const createPublishPreview = async (
  accountId: string,
  dynamicPageId: string,
  options?: { quizId?: string },
): Promise<PublishPreview> => {
  const fn = httpsCallable<unknown, PublishPreview>(
    functions,
    'accounts/campaigns/versions/createPublishPreview',
    { timeout: 120000 },
  );

  const res = await fn({ accountId, dynamicPageId, ...options });

  return res.data;
};

/**
 * Expands one entity of a publish preview into the fields that changed. The
 * preview summary deliberately omits the per-field payloads (that is what keeps
 * it small), so the panel asks for a row's detail only when it is opened.
 */
/**
 * One expanded entity. Carries the row's own identifying fields as well as its
 * detail, so a batched response can be matched back to the rows that asked.
 */
export type PreviewEntityDiff = NormalizedEntityDiff & {
  entity: DynamicPageVersionDiff['entity'];
  type: DynamicPageVersionDiff['type'];
  id: string;
  name: string;
  explanation: string;
  rawText?: RawTextChange;
};

/**
 * Expands several of a preview's entities in one call, so opening any row and
 * stepping between them costs one wait rather than one per row. Entities that
 * would push the response past the server's ceiling are left out; ask for those
 * individually.
 */
export const getPreviewEntityDiffs = async (params: {
  accountId: string;
  dynamicPageId: string;
  previewId: string;
  entities: { id: string; kind: string }[];
  includeRawText?: boolean;
}): Promise<{ changes: PreviewEntityDiff[]; omitted?: number }> => {
  const fn = httpsCallable<
    unknown,
    { changes: PreviewEntityDiff[]; omitted?: number }
  >(functions, 'accounts/campaigns/versions/getPreviewEntityDiff', {
    timeout: 120000,
  });

  const res = await fn(params);

  return { changes: res.data.changes ?? [], omitted: res.data.omitted };
};

/**
 * Names a preview's changes with an LLM. Deliberately a separate call from
 * {@link createPublishPreview} so the diff renders as soon as it is ready and
 * the suggestion fills the fields in when it arrives. The server reads the
 * preview it already holds, so the model sees the changed fields and not just
 * the summary lines. Never rejects — failing to name a version must not stand
 * between the user and publishing.
 */
export const suggestVersionMeta = async (
  accountId: string,
  dynamicPageId: string,
  previewId: string,
): Promise<{ name: string; description: string }> => {
  const fn = httpsCallable<unknown, { name: string; description: string }>(
    functions,
    'accounts/campaigns/versions/suggestVersionMeta',
    { timeout: 60000 },
  );

  try {
    const res = await fn({ accountId, dynamicPageId, previewId });

    return res.data;
  } catch (error) {
    console.error('Failed to suggest a version name:', error);
    return {
      name: `Update ${new Date().toISOString().slice(0, 10)}`,
      description: '',
    };
  }
};

/**
 * Publishes a preview by id. The server already holds the snapshot, so nothing
 * is re-sent and what goes live is exactly what was previewed.
 */
export const publishPreview = async (
  accountId: string,
  dynamicPageId: string,
  previewId: string,
  name: string,
  description?: string,
): Promise<{ versionId: string; hasPendingChanges: boolean }> => {
  const fn = httpsCallable<
    unknown,
    { versionId: string; hasPendingChanges: boolean }
  >(functions, 'accounts/campaigns/versions/publishPreview', {
    timeout: 120000,
  });

  const res = await fn({
    accountId,
    dynamicPageId,
    previewId,
    name,
    description,
  });

  return res.data;
};

export const replaceAllDynamicPagesLinks = async (
  accountId: string,
  dynamicPageIds: string[],
  mappings: { link: string; toLink: string }[],
): Promise<any> => {
  const fn = httpsCallable<any, any>(
    functions,
    'accounts/campaigns/linkReplacer/replaceLinks',
    { timeout: 600000 },
  );

  const res = await fn({
    accountId,
    campaignIds: dynamicPageIds,
    mappings,
  });

  return res.data;
};

/**
 * Publishes a single dynamic page as part of a bulk publish, prefixing the
 * version name with "Bulk - ". The server snapshots, diffs and publishes, so
 * no version blob crosses the wire.
 */
export const bulkPublishDynamicPage = async (
  accountId: string,
  dynamicPageId: string,
  versionName: string,
  versionDescription: string,
): Promise<void> => {
  const preview = await createPublishPreview(accountId, dynamicPageId);

  await publishPreview(
    accountId,
    dynamicPageId,
    preview.previewId,
    'Bulk - ' + versionName,
    versionDescription,
  );
};
